import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageShell } from '@/components/layout/PageShell';
import { Icon } from '@/components/ui/Icon';
import { SearchField } from '@/kit';
import { GridTile } from '@/components/feed/GridTile';
import { capture } from '@/modules/heat/signals';
import { useProfiles } from '@/modules/profiles/useProfiles';
import { cn } from '@/lib/cn';

type Filter = 'all' | 'live' | 'pulse';

/**
 * Grid — the dense contact-sheet wall.
 *
 * Mobile: 3 cols, edge-to-edge. Desktop: 4 / 5 / 6 cols. Hairline ink seams
 * between tiles. Headline + filter pills only — the wall is the point.
 */
export const GridPage = () => {
  const [filter, setFilter] = useState<Filter>('all');
  const [params, setParams] = useSearchParams();
  const { profiles, loading } = useProfiles();

  const query = params.get('q') ?? '';

  useEffect(() => {
    capture('surface_enter', 'grid');
  }, []);

  const tiles = useMemo(() => {
    let list = profiles;
    if (filter === 'live') list = list.filter((p) => p.presence === 'live');
    if (filter === 'pulse') list = list.filter((p) => p.band === 'pulse');

    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((p) =>
        [p.handle, p.location, p.distance].some((field) =>
          String(field ?? '').toLowerCase().includes(q),
        ),
      );
    }
    return list;
  }, [filter, profiles, query]);

  const onSearch = (next: string) => {
    if (next) setParams({ q: next }, { replace: true });
    else setParams({}, { replace: true });
  };

  const liveCount = profiles.filter((p) => p.presence === 'live').length;
  const pulseCount = profiles.filter((p) => p.band === 'pulse').length;

  return (
    <PageShell
      mobileTopBar={
        <>
          {/* Identity is the WorldSwitcher in PageShell's bar — one lockup.
              This spacer keeps the live count on the right edge. */}
          <span aria-hidden />
          <div className="flex items-center gap-1.5 font-display text-[10px] font-bold uppercase tracking-widest text-pink">
            <Icon name="bolt" size={14} />
            <span>{liveCount} live</span>
          </div>
        </>
      }
    >
      {/* HEADLINE + filter pills */}
      <section className="px-5 pb-5 pt-7 md:px-12 md:pb-7 md:pt-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h1
            className="font-display font-black uppercase tracking-display"
            style={{
              fontSize: 'clamp(36px, 6vw, 92px)',
              lineHeight: 0.88,
              letterSpacing: '-0.05em',
            }}
          >
            Who&rsquo;s around<span className="text-pink">.</span>
          </h1>
          <div className="flex items-center gap-2">
            <FilterPill active={filter === 'all'} onClick={() => setFilter('all')}>
              All · {profiles.length}
            </FilterPill>
            <FilterPill active={filter === 'live'} onClick={() => setFilter('live')}>
              Live · {liveCount}
            </FilterPill>
            <FilterPill active={filter === 'pulse'} onClick={() => setFilter('pulse')}>
              Pulse · {pulseCount}
            </FilterPill>
          </div>
        </div>

        {/*
          Search is an explicit utility on the wall, not a hidden control.
          `.men` was clearer that browsing and searching are tools; this brings
          that explicitness across without touching the feed's discovery-led feel.
        */}
        <div className="mt-6 max-w-[420px]">
          <SearchField
            key={query}
            defaultValue={query}
            placeholder="Search people, places and interests"
            label="Search people"
            onSubmit={onSearch}
          />
        </div>
      </section>

      {/* DENSE WALL */}
      <section className="px-0 md:px-12 md:pb-6">
        <div
          className={cn(
            'grid gap-px bg-white/[0.04]',
            'grid-cols-3',
            'md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6',
          )}
        >
          {tiles.map((p, i) => (
            <GridTile key={p.id} profile={p} index={i} />
          ))}
        </div>
      </section>

      {loading && profiles.length === 0 ? null : tiles.length === 0 ? (
        <div className="flex flex-col items-center gap-3 px-5 py-16 text-center md:px-12">
          <Icon name="search_off" size={28} className="text-white/20" />
          <p className="font-display text-[11px] font-bold uppercase tracking-hottr-label text-text-secondary">
            {profiles.length === 0
              ? 'Nobody here yet. Real people only.'
              : `Nobody matches ${query ? `“${query}”` : 'that filter'}`}
          </p>
          {profiles.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setFilter('all');
                onSearch('');
              }}
              className="font-display text-[10px] font-bold uppercase tracking-hottr-label text-hottr-pink hover:underline"
            >
              Clear and show everyone
            </button>
          )}
        </div>
      ) : (
        <p className="px-5 py-10 text-center font-display text-[9px] font-bold uppercase tracking-widest text-text-muted md:px-12">
          Tap a tile<span className="text-pink">.</span>
        </p>
      )}
    </PageShell>
  );
};

const FilterPill = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    className={cn(
      'border px-3 py-1.5 font-display text-[10px] font-bold uppercase tracking-widest',
      'transition-[border-color,color,background] duration-fast ease-signal',
      active
        ? 'border-pink bg-pink/10 text-pink shadow-glow-soft'
        : 'border-white/15 text-text-secondary hover:border-pink/40 hover:text-text-primary',
    )}
  >
    {children}
  </button>
);
